/**
 * multileaderConverter — MULTILEADER → CadPrimitive[] (PDF-5b).
 *
 * REUSES the pure scene adapter `scene3d/dimensions/multileaderToPrimitives`
 * (CLAUDE.md PRIORITY #1 — full fidelity, no simplification): the adapter walks
 * EVERY leader root → node → line (multi-leader hierarchy, not just the first),
 * splines via Catmull-Rom cubic Bezier, dogleg + landing per the MLEADERSTYLE
 * override flags + annotative context, MText content via the shared MTEXT
 * parser, and block content via the recursive block flatten.
 *
 * Pipeline:
 *   1. typed `MultileaderEntity` → `EntityPreview` property-bag via the
 *      canonical bridge `buildShellEntityPreview` (state/dxfGeometry), which
 *      encodes the full leader hierarchy + MLENT_* overrides the adapter reads.
 *   2. call `multileaderToPrimitives` → `MultileaderPrimitive[]`.
 *   3. UN-OFFSET every coordinate (`unapplyOffset`) → RAW world mm (§4.1, like
 *      the dimension converter).
 *   4. map each kind → CadPrimitive:
 *        leader-line (straight)  → stroked polyline `PathPrimitive`;
 *        leader-line (spline)    → stroked Catmull-Rom cubic bezier path;
 *        dogleg / landing        → stroked line `PathPrimitive`;
 *        arrow                   → FILL-ONLY arrowhead (or stroked outline);
 *        mtext-content           → `TextRunPrimitive` per MText run;
 *        block-content           → reuse `insertToPrimitives` over a synthesized
 *                                  `BlockInsertEntity` (the block reference at
 *                                  the content anchor / scale / rotation), so
 *                                  the block geometry plots through the SAME
 *                                  tested INSERT→IR path as a top-level INSERT.
 *
 * Colour: per-primitive `colorHex` resolved by the adapter (leader colour,
 * MText colour) → plot RGB (white→black). Block content inherits the synthesized
 * INSERT's colour, resolved by `insertToPrimitives`.
 */

import type {
  BlockInsertEntity,
  MultileaderEntity,
} from "@dxf-ai-editor/drawing-model";

import { buildShellEntityPreview } from "../../../state/dxfGeometry";
import { unapplyOffset } from "../../../scene3d/coordinateOrigin";
import { multileaderToPrimitives } from "../../../scene3d/dimensions/multileaderToPrimitives";
import type { MultileaderAdapterContext } from "../../../scene3d/dimensions/multileaderToPrimitives";
import type {
  MTextRun,
  MultileaderPrimitive,
  Vec3,
} from "../../../scene3d/types";

import { hexToPlotRgb } from "../pdfTransform";
import type {
  CadConvertContext,
  CadPrimitive,
  PathPrimitive,
  PathSegment,
  Point2,
  PlotStyle,
} from "../cadPrimitive";
import { resolvePlotStyle } from "./resolveStyle";
import { insertToPrimitives } from "./insertConverter";
import {
  arrowFillPolygon,
  arrowOutlinePolyline,
  isFilledArrow,
} from "./arrowGeometry";

/** Map an offset-world adapter Vec3 back to a RAW world {@link Point2}. */
function toRawPoint(v: Vec3): Point2 {
  const w = unapplyOffset(v);
  return { x: w.x, y: w.y };
}

/** Catmull-Rom → cubic Bézier control points for the segment p1→p2. */
function catmullRomBezier(
  p0: Point2,
  p1: Point2,
  p2: Point2,
  p3: Point2,
): { c1: Point2; c2: Point2 } {
  // Standard uniform Catmull-Rom → Bézier tangent (1/6 of the chord triple).
  return {
    c1: { x: p1.x + (p2.x - p0.x) / 6, y: p1.y + (p2.y - p0.y) / 6 },
    c2: { x: p2.x - (p3.x - p1.x) / 6, y: p2.y - (p3.y - p1.y) / 6 },
  };
}

/** Build a spline path (Catmull-Rom cubic beziers) through the vertices. */
function splinePath(
  entityId: string,
  layer: string,
  pts: Point2[],
  style: PlotStyle,
): PathPrimitive | null {
  if (pts.length < 2) return null;
  if (pts.length === 2) return strokedPath(entityId, layer, pts, style);
  const segments: PathSegment[] = [];
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[Math.max(0, i - 1)]!;
    const p1 = pts[i]!;
    const p2 = pts[i + 1]!;
    const p3 = pts[Math.min(pts.length - 1, i + 2)]!;
    const { c1, c2 } = catmullRomBezier(p0, p1, p2, p3);
    segments.push({ kind: "bezier", c1, c2, to: p2 });
  }
  return {
    type: "path",
    entityId,
    layer,
    start: pts[0]!,
    segments,
    closed: false,
    fill: "none",
    style,
  };
}

/** A stroked open polyline `PathPrimitive`. */
function strokedPath(
  entityId: string,
  layer: string,
  points: Point2[],
  style: PlotStyle,
): PathPrimitive | null {
  if (points.length < 2) return null;
  const segments: PathSegment[] = [];
  for (let i = 1; i < points.length; i++) {
    segments.push({ kind: "line", to: points[i]! });
  }
  return {
    type: "path",
    entityId,
    layer,
    start: points[0]!,
    segments,
    closed: false,
    fill: "none",
    style,
  };
}

function styleWithColor(base: PlotStyle, colorHex: string): PlotStyle {
  return { ...base, color: hexToPlotRgb(colorHex) };
}

/** Convert one MText run anchored at `anchor` into a `TextRunPrimitive`. */
function mtextRunToPrimitive(
  entityId: string,
  layer: string,
  run: MTextRun,
  anchor: Point2,
  rotation: number,
  fallbackColorHex: string,
  mirrored: boolean,
): CadPrimitive {
  return {
    type: "text-run",
    entityId,
    layer,
    position: anchor,
    height: run.heightWorld,
    rotation,
    widthFactor: run.widthFactor,
    obliqueAngle: run.oblique,
    // MText content attaches at the landing/content anchor (left-baseline is
    // the AutoCAD default attachment for left-attached MTEXT content).
    hJustification: 0,
    vJustification: 0,
    styleName: run.fontStyleName,
    value: run.text,
    color: hexToPlotRgb(run.colorHex ?? fallbackColorHex),
    // GitHub Codex review PR #558 round 2 P2 — carry the MIRRTEXT
    // handedness flag into the PDF text-run IR (see `TextRunPrimitive
    // .mirrored`'s own doc).
    mirrored,
  };
}

/**
 * Convert a MULTILEADER to its CadPrimitive list. Returns `[]` only when the
 * adapter produced no primitives.
 */
export function multileaderToPdfPrimitives(
  entity: MultileaderEntity,
  ctx: CadConvertContext,
): CadPrimitive[] {
  // 1. typed entity → EntityPreview via the canonical bridge.
  const preview = buildShellEntityPreview(entity, new Map(), {}, ctx.blocks);

  // 2. call the pure scene adapter.
  const adapterCtx: MultileaderAdapterContext = {
    layerColors: ctx.layerColors,
    blockColor: ctx.blockColor,
    cameraZoom: 1,
    zOrderIndex: 0,
    blocks: ctx.blocks,
    annotationScale: null,
  };
  const renderable = multileaderToPrimitives(preview, adapterCtx);
  if (renderable.geometry.kind !== "multileader") return [];
  const primitives = renderable.geometry.primitives;

  const baseStyle = resolvePlotStyle(entity, ctx);
  // PDF-6.3b — CTB/STB do-not-plot honour.
  if (baseStyle.skip) return [];
  // PDF-6.2a: every leader sub-primitive (lines / dogleg / arrows / MText runs)
  // files under the MULTILEADER's own layer. Block content recurses through
  // `insertToPrimitives` (synthesised INSERT carries `layer: entity.layer`), so
  // its children get the same layer-0-inheritance treatment automatically.
  const layer = entity.layer ?? "0";

  const out: CadPrimitive[] = [];
  for (const p of primitives) {
    pushPrimitive(out, entity, layer, p, baseStyle, ctx);
  }
  return out;
}

function pushPrimitive(
  out: CadPrimitive[],
  entity: MultileaderEntity,
  layer: string,
  p: MultileaderPrimitive,
  baseStyle: PlotStyle,
  ctx: CadConvertContext,
): void {
  switch (p.kind) {
    case "leader-line": {
      const pts = p.vertices.map(toRawPoint);
      const style = styleWithColor(baseStyle, p.colorHex);
      const path = p.spline
        ? splinePath(entity.id, layer, pts, style)
        : strokedPath(entity.id, layer, pts, style);
      if (path) out.push(path);
      return;
    }
    case "dogleg":
    case "landing": {
      const path = strokedPath(
        entity.id,
        layer,
        [toRawPoint(p.a), toRawPoint(p.b)],
        styleWithColor(baseStyle, p.colorHex),
      );
      if (path) out.push(path);
      return;
    }
    case "arrow": {
      const style = styleWithColor(baseStyle, p.colorHex);
      const tip = toRawPoint(p.tip);
      const dir = { x: p.direction.x, y: p.direction.y };
      if (isFilledArrow(p.variant)) {
        const poly = arrowFillPolygon(tip, dir, p.size, p.variant);
        if (poly.length >= 3) {
          out.push({
            type: "path",
            entityId: entity.id,
            layer,
            start: poly[0]!,
            segments: poly.slice(1).map((pt) => ({ kind: "line", to: pt })),
            closed: true,
            fill: "nonzero",
            stroke: false,
            style,
          });
        }
        return;
      }
      const poly = arrowOutlinePolyline(tip, dir, p.size, p.variant);
      const path = strokedPath(entity.id, layer, poly, style);
      if (path) out.push(path);
      return;
    }
    case "mtext-content": {
      const anchor = toRawPoint(p.anchor);
      for (const run of p.runs) {
        out.push(
          mtextRunToPrimitive(
            entity.id,
            layer,
            run,
            anchor,
            p.rotation,
            p.colorHex,
            p.mirrored,
          ),
        );
      }
      return;
    }
    case "block-content": {
      // Reuse the tested INSERT→IR path: synthesize a Block reference at the
      // content anchor / scale / rotation. The block's children plot through
      // `insertToPrimitives` exactly like a top-level INSERT — full fidelity,
      // no flatten-collapse (the adapter's `flattened` RenderableEntity list is
      // WebGL-coupled; we re-derive from the typed block definition instead).
      const anchor = toRawPoint(p.anchor);
      const synthetic: BlockInsertEntity = {
        id: `${entity.id}:mleader-block`,
        spaceId: entity.spaceId,
        type: "Block",
        layer: entity.layer,
        color: entity.color,
        linetype: entity.linetype,
        lineweight: entity.lineweight,
        transparency: entity.transparency,
        blockName: p.blockName,
        insertionPoint: { x: anchor.x, y: anchor.y },
        scaleX: p.scale,
        scaleY: p.scale,
        scaleZ: p.scale,
        rotation: p.rotation,
        columnCount: 1,
        rowCount: 1,
        columnSpacing: 0,
        rowSpacing: 0,
        extrusion: { x: 0, y: 0, z: 1 },
        attributes: [],
        customProperties: {},
        annotationScales: [],
        annotativeContexts: [],
      };
      for (const cp of insertToPrimitives(synthetic, ctx)) out.push(cp);
      return;
    }
  }
}
